/**
 * Session Storage for Grid State
 * Extends BaseSessionStorageManager for grid-specific functionality
 */

import { CONFIG } from '../../core/config.js';
import { logDebug } from '../../core/error-handler.js';
import {
    BaseSessionStorageManager,
    clearByPrefix,
    getCurrentPageIdentifier
} from '../../core/session-storage-utils.js';

/**
 * Grid Session Storage Manager class
 * Extends BaseSessionStorageManager for grid-specific functionality
 */
class GridSessionStorageManager extends BaseSessionStorageManager {
    constructor() {
        super(CONFIG.SESSION_STORAGE.GRID_PREFIX);
    }

    /**
     * Save grid state to session storage
     * @param {string} pageIdentifier - Unique identifier for the page
     * @param {string} gridKey - Unique identifier for the grid
     * @param {Object} state - Grid state to save
     * @returns {boolean} Success status
     */
    save(pageIdentifier, gridKey, state) {
        const result = super.save(pageIdentifier, state, gridKey);
        if (result) {
            logDebug('GridStateStorage: Saved', { pageIdentifier, gridKey });
        }
        return result;
    }

    /**
     * Load grid state from session storage
     * @param {string} pageIdentifier - Unique identifier for the page
     * @param {string} gridKey - Unique identifier for the grid
     * @returns {Object|null} Grid state or null if not found/expired
     */
    load(pageIdentifier, gridKey) {
        const result = super.load(pageIdentifier, gridKey);
        if (result) {
            logDebug('GridStateStorage: Loaded', { pageIdentifier, gridKey });
        }
        return result;
    }

    /**
     * Remove grid state from session storage
     * @param {string} pageIdentifier - Unique identifier for the page
     * @param {string} gridKey - Unique identifier for the grid
     * @returns {boolean} Success status
     */
    remove(pageIdentifier, gridKey) {
        const result = super.remove(pageIdentifier, gridKey);
        if (result) {
            logDebug('GridStateStorage: Removed', { pageIdentifier, gridKey });
        }
        return result;
    }
}

export const gridSessionStorageManager = new GridSessionStorageManager();

function resolveGridKey(grid) {
    // Prefer Kendo grid options.name, fallback to element id
    if (grid && grid.options && grid.options.name) return grid.options.name;
    const element = grid && grid.element;
    const id = element && element.attr ? element.attr('id') : element && element.id;
    return id || 'grid';
}

function resolveGridKeyFromSelector(selector) {
    if (!selector) return 'grid';
    if (typeof selector === 'string') {
        // If it's an ID selector like '#usersGrid', use the id part
        if (selector.startsWith('#')) {
            return selector.substring(1);
        }
        // For other selectors, sanitize to a stable key
        return selector.replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/^_+|_+$/g, '') || 'grid';
    }
    // If it's a DOM element or jQuery object, try to read its id
    const $el = selector instanceof jQuery ? selector : $(selector);
    const id = $el && $el.attr ? $el.attr('id') : $el && $el[0] && $el[0].id;
    return id || 'grid';
}

export const GridStateStorage = {
    isAvailable() {
        return gridSessionStorageManager.isAvailable();
    },
    saveForGrid(grid, state) {
        const page = getCurrentPageIdentifier();
        const key = resolveGridKey(grid);
        return gridSessionStorageManager.save(page, key, state);
    },
    loadForGrid(grid) {
        const page = getCurrentPageIdentifier();
        const key = resolveGridKey(grid);
        return gridSessionStorageManager.load(page, key);
    },
    removeForGrid(grid) {
        const page = getCurrentPageIdentifier();
        const key = resolveGridKey(grid);
        return gridSessionStorageManager.remove(page, key);
    },
    saveForSelector(selector, state) {
        const page = getCurrentPageIdentifier();
        const key = resolveGridKeyFromSelector(selector);
        return gridSessionStorageManager.save(page, key, state);
    },
    loadForSelector(selector) {
        const page = getCurrentPageIdentifier();
        const key = resolveGridKeyFromSelector(selector);
        return gridSessionStorageManager.load(page, key);
    },
    removeForSelector(selector) {
        const page = getCurrentPageIdentifier();
        const key = resolveGridKeyFromSelector(selector);
        return gridSessionStorageManager.remove(page, key);
    },
    clearAll() {
        return clearByPrefix(CONFIG.SESSION_STORAGE.GRID_PREFIX);
    }
};

export default GridStateStorage;
